import React, { useState } from 'react';
import { format } from 'date-fns';

// Define the interaction types for compliance logging
type InteractionType =
  | 'note'
  | 'email'
  | 'phone_call'
  | 'in_person'
  | 'document'
  | 'system'
  | 'onboarding';

// Interface for a compliance log entry
interface ComplianceLogEntry {
  id: string;
  timestamp: Date;
  type: InteractionType;
  description: string;
  staff: string;
  details?: string;
}

// Mock data for demonstration
const mockComplianceData: ComplianceLogEntry[] = [
  {
    id: '1',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    type: 'note',
    description: 'Added note about retirement planning',
    staff: 'Jane Smith',
    details: 'Member expressed interest in increasing 401k contributions',
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    type: 'phone_call',
    description: 'Outbound call to discuss loan options',
    staff: 'John Doe',
    details: 'Call duration: 12 minutes. Member inquired about auto loan rates.',
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    type: 'email',
    description: 'Sent quarterly newsletter',
    staff: 'System',
    details: 'Email opened: Yes, Click-through: Yes',
  },
  {
    id: '4',
    timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    type: 'in_person',
    description: 'Branch visit for account review',
    staff: 'Maria Johnson',
    details: 'Updated contact information and discussed savings goals',
  },
  {
    id: '5',
    timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    type: 'document',
    description: 'Loan application submitted',
    staff: 'System',
    details: 'Application ID: LOAN-2023-05678',
  },
  {
    id: '6',
    timestamp: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    type: 'onboarding',
    description: 'Account verification completed',
    staff: 'System',
    details: 'Identity verification passed',
  },
  {
    id: '7',
    timestamp: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    type: 'system',
    description: 'Account created',
    staff: 'System',
    details: 'Initial setup completed',
  },
];

// Get the appropriate icon and color for each interaction type
const getInteractionTypeStyles = (
  type: InteractionType
): { icon: JSX.Element; bgColor: string; textColor: string } => {
  switch (type) {
    case 'note':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
      };
    case 'email':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        ),
        bgColor: 'bg-indigo-100',
        textColor: 'text-indigo-700',
      };
    case 'phone_call':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
        ),
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
      };
    case 'in_person':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
      };
    case 'document':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
      };
    case 'system':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3 1h10v8H5V6z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
      };
    case 'onboarding':
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
            <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
          </svg>
        ),
        bgColor: 'bg-brand-blue/10',
        textColor: 'text-brand-blue',
      };
    default:
      return {
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        ),
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-700',
      };
  }
};

// Filter options for the compliance log
type FilterOption = 'all' | InteractionType;

const ComplianceLogSection: React.FC = () => {
  const [filter, setFilter] = useState<FilterOption>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filter the log entries based on the selected filter
  const filteredLogs =
    filter === 'all'
      ? mockComplianceData
      : mockComplianceData.filter(entry => entry.type === filter);

  // Function to handle exporting the log
  const handleExport = (format: 'pdf' | 'csv') => {
    // In a real implementation, this would generate and download the file
    console.log(`Exporting compliance log as ${format}`);
    alert(
      `In a production environment, this would download a ${format.toUpperCase()} file with the compliance log.`
    );
  };

  return (
    <div className="space-y-4">
      {/* Header with title and export buttons */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-slate-800">Member Interaction Log</h3>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('pdf')}
            className="bg-brand-blue hover:bg-brand-blue/90 text-white text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                clipRule="evenodd"
              />
            </svg>
            Export PDF
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'all'
              ? 'bg-slate-700 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('note')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'note'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          Notes
        </button>
        <button
          onClick={() => setFilter('email')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'email'
              ? 'bg-indigo-600 text-white'
              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }`}
        >
          Emails
        </button>
        <button
          onClick={() => setFilter('phone_call')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'phone_call'
              ? 'bg-green-600 text-white'
              : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          Phone Calls
        </button>
        <button
          onClick={() => setFilter('in_person')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'in_person'
              ? 'bg-purple-600 text-white'
              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
          }`}
        >
          In-Person
        </button>
        <button
          onClick={() => setFilter('document')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'document'
              ? 'bg-yellow-600 text-white'
              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
          }`}
        >
          Documents
        </button>
        <button
          onClick={() => setFilter('system')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'system'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          System
        </button>
        <button
          onClick={() => setFilter('onboarding')}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            filter === 'onboarding'
              ? 'bg-brand-blue text-white'
              : 'bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20'
          }`}
        >
          Onboarding
        </button>
      </div>

      {/* Log entries */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {filteredLogs.length > 0 ? (
          filteredLogs.map(entry => {
            const { icon, bgColor, textColor } = getInteractionTypeStyles(entry.type);
            const isExpanded = expanded === entry.id;

            return (
              <div
                key={entry.id}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
              >
                {/* Entry header - always visible */}
                <div
                  className="p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(isExpanded ? null : entry.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`${bgColor} ${textColor} p-2 rounded-full flex-shrink-0`}>
                      {icon}
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{entry.description}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {format(entry.timestamp, 'MMM d, yyyy')} at{' '}
                            {format(entry.timestamp, 'h:mm a')}
                          </p>
                        </div>
                        <div className="flex items-center">
                          <span
                            className={`text-xs ${textColor} px-2 py-0.5 rounded-full ${bgColor}`}
                          >
                            {entry.type.replace('_', ' ')}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 text-slate-400 ml-2 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0">
                    <div className="pl-9 border-l-2 border-slate-100 ml-3">
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-xs font-medium text-slate-700">Staff Member</p>
                          <p className="text-xs text-slate-600">{entry.staff}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-700 mb-1">Details</p>
                          <p className="text-xs text-slate-600">
                            {entry.details || 'No additional details recorded.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 bg-slate-50 rounded-lg">
            <p className="text-slate-500">No interactions found matching the selected filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceLogSection;
